import { useTranslation } from 'react-i18next';
import ReactECharts from "echarts-for-react";
import { isoTimeToLocalString } from "../../Trends/trendUtil";
import { useMemo } from 'react';
import deepMerge from '../../../util/deepMerge';
import { meanByIndex } from '../utils';

export default function OccupancyHeatmap({ occupancyEventData, optionsOverrides }) {

    const HEIGHT_PER_CATEGORY = 25

    const { t } = useTranslation();

    const series = [];
    const daysOfWeek = [
        t("Sun"), t("Mon"), t("Tue"), t("Wed"), t("Thu"), t("Fri"), t("Sat")
    ]

    let hoursOfDay = []
    for (let hour = 0; hour < 24; hour++) {
        hoursOfDay.push(`${hour}:00`)
    }

    const timestampMeanData = occupancyEventData.points_data[0] ? meanByIndex(occupancyEventData.points_data.map(point => point.values)) : [];

    const seriesData = Array.from({ length: daysOfWeek.length }, () =>
        Array.from({ length: hoursOfDay.length }, () => ({ sum: 0, count: 0 }))
    );

    occupancyEventData.timestamps.forEach((timestamp, index) => {
        let datetime = new Date(timestamp);
        let dayOfWeek = datetime.getDay(); // 0 (Sun) to 6 (Sat)
        let hourOfDay = datetime.getHours(); // 0 to 23

        seriesData[dayOfWeek][hourOfDay] = {
            sum: seriesData[dayOfWeek][hourOfDay].sum + timestampMeanData[index],
            count: seriesData[dayOfWeek][hourOfDay].count + 1
        }
    })

    const heatmapData = [];
    seriesData.forEach((dayData, dayIndex) => {
        dayData.forEach((hourData, hourIndex) => {
            const value = hourData.count === 0
                ? null
                : (hourData.sum / hourData.count) * 100;
            heatmapData.push([hourIndex, dayIndex, value]);
        });
    });

    series.push({
        data: heatmapData,
        type: "heatmap",
        itemStyle: {
            normal: {
                borderColor: "#DDDDDD",
                borderWidth: 1
            }
        },
        label: {
            show: true,
            formatter: (param) => {
                const val = param.value[2];
                return val == null ? "–" : `${val.toFixed(1)}%`;
            }
        }
    });



    const options = {
        tooltip: {
            position: "top",
            confine: true,
            valueFormatter: (value) => (typeof value === "number" ? `${value.toFixed(1)}%` : value),
        },
        grid: {
            top: 50,
            left: '4%',
            containLabel: true,
            right: '5%',
            bottom: "4%"
        },
        xAxis: {
            type: "category",
            data: hoursOfDay,
            position: "top",
            axisLine: { show: false },
            axisTick: { show: false },
            splitArea: {
                show: true,
            },
            splitLine: {
                show: true,
                lineStyle: { color: "#B0B0B0", width: 1 }
            },
            axisLabel: { interval: 0, hideOverlap: false }
        },
        yAxis: {
            type: "category",
            data: daysOfWeek,
            inverse: true,
            axisLine: { show: false },
            axisTick: { show: false },
            splitArea: {
                show: true,
            },
            splitLine: {
                show: true,
                lineStyle: { color: "#B0B0B0", width: 1 }
            },
            axisLabel: { interval: 0, hideOverlap: false }
        },
        textStyle: {
            fontFamily: "Roboto, Helvetica, Arial, sans-serif",
            fontSize: 12
        },
        series: series,
    };

    // Continuous color mapping from white (0) to deep blue (1)
    options.visualMap = {
        type: "continuous",
        min: 0,
        max: 100,
        calculable: true,
        realtime: false,
        orient: "horizontal",
        left: "center",
        bottom: "0%",
        inRange: {
            color: [
                "#DFF1FC",
                "#003063"
            ]
        },
        textStyle: {
            color: "black",
        },
        show: false,
    };

    const mergedOptions = useMemo(() => {
        if (optionsOverrides) {
            return deepMerge(options, optionsOverrides);
        }
        return options;

    }, [options, optionsOverrides]);

    return (
        <ReactECharts option={mergedOptions} style={{ height: 100 + (daysOfWeek?.length || 0) * HEIGHT_PER_CATEGORY }} notMerge={true} />
    );
}
